import { FC } from 'react'
import { useTranslation } from 'react-i18next'

import { ArrowDownward, ArrowUpward, Comment, Delete, Edit } from '@mui/icons-material'
import { Box, Button, Card, CardActions, CardContent, Checkbox, IconButton, Link, Stack, Tooltip, Typography } from '@mui/material'

import { borderRadiusDefault } from '../../../../../themes/theme'
import { getThumbnailUrl } from '../../helpers/screenshotHelpers'
import { AnalysisScreenshotView } from '../../types/AnalysisScreenshots'
import { ScreenshotFeatureList } from '../ScreenshotFeatureList/ScreenshotFeatureList'

type ScreenshotCardProps = {
  view: AnalysisScreenshotView
  position: number
  checked: boolean
  highlighted?: boolean
  disabled?: boolean
  // Checking, editing and deleting wait while an order isn't saved.
  lockActions?: boolean
  moveDisabled?: boolean
  canMoveUp: boolean
  canMoveDown: boolean
  commentUrl: string
  onCheck: (checked: boolean) => void
  onMoveUp: () => void
  onMoveDown: () => void
  onEdit: () => void
  onComment: () => void
  onDelete: () => void
}

export const screenshotCardId = (screenshotId: string) => `analysis-screenshot-${screenshotId}`

export const ScreenshotCard: FC<ScreenshotCardProps> = ({
  view,
  position,
  checked,
  highlighted,
  disabled,
  lockActions,
  moveDisabled,
  canMoveUp,
  canMoveDown,
  commentUrl,
  onCheck,
  onMoveUp,
  onMoveDown,
  onEdit,
  onComment,
  onDelete,
}) => {
  const { t } = useTranslation()
  const { item } = view
  const label = t('internal-analysts:analysis_screenshots_card_label', { position })

  return (
    <Card
      id={screenshotCardId(item.screenshotId)}
      variant="outlined"
      role="group"
      aria-label={label}
      sx={{ height: '100%', display: 'flex', flexDirection: 'column', borderColor: highlighted ? 'primary.main' : undefined, borderWidth: highlighted ? 2 : 1 }}
    >
      <Stack direction="row" alignItems="center" spacing={0.5} px={1} pt={1}>
        <Checkbox
          size="small"
          checked={checked}
          disabled={disabled || lockActions}
          onChange={(event) => onCheck(event.target.checked)}
          inputProps={{ 'aria-label': t('internal-analysts:analysis_screenshots_select_label', { position }) }}
        />
        <Typography fontWeight="bold">{position}.</Typography>
        <Box flex={1} />
        <Tooltip title={t('internal-analysts:analysis_screenshots_move_up')}>
          <span>
            <IconButton
              size="small"
              aria-label={t('internal-analysts:analysis_screenshots_move_up_label', { position })}
              disabled={disabled || moveDisabled || !canMoveUp}
              onClick={onMoveUp}
            >
              <ArrowUpward fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title={t('internal-analysts:analysis_screenshots_move_down')}>
          <span>
            <IconButton
              size="small"
              aria-label={t('internal-analysts:analysis_screenshots_move_down_label', { position })}
              disabled={disabled || moveDisabled || !canMoveDown}
              onClick={onMoveDown}
            >
              <ArrowDownward fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      </Stack>
      <Link href={item.url} target="_blank" rel="noopener noreferrer" sx={{ display: 'block', px: 1 }}>
        <Box
          component="img"
          src={getThumbnailUrl(item.url)}
          alt={label}
          loading="lazy"
          sx={{ width: '100%', height: 220, objectFit: 'contain', bgcolor: 'grey.900', display: 'block', borderRadius: borderRadiusDefault }}
        />
      </Link>
      <CardActions sx={{ flexWrap: 'wrap', gap: 0.5 }}>
        <Button size="small" startIcon={<Edit />} disabled={disabled || lockActions} onClick={onEdit}>
          {t('internal-analysts:analysis_screenshots_edit')}
        </Button>
        <Button size="small" color="error" startIcon={<Delete />} disabled={disabled || lockActions} onClick={onDelete}>
          {t('internal-analysts:analysis_screenshots_remove')}
        </Button>
        <Button size="small" startIcon={<Comment />} href={commentUrl} target="_blank" rel="noopener noreferrer" onClick={onComment}>
          {t(item.commentId ? 'internal-analysts:analysis_screenshots_edit_comment' : 'internal-analysts:analysis_screenshots_add_comment')}
        </Button>
      </CardActions>
      <CardContent sx={{ pt: 0, flex: 1 }}>
        <ScreenshotFeatureList
          features={view.features.map((feature) => ({ feature }))}
          concepts={view.concepts.map((concept) => ({ concept }))}
          unknownConceptIds={view.unknownConceptIds}
        />
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1, wordBreak: 'break-all' }}>
          {item.url}
        </Typography>
      </CardContent>
    </Card>
  )
}
